import { isEmpty, isNil } from 'lodash';
import { useContext } from 'react';
import { MBClearButton } from '@shared/components/MBButton';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { MBIconV2, MBImage } from '@shared/components/image';
import {
  getComponentIconWithFallback,
  getComponentLabel,
  getComponentPlaceholder,
  isDisplayableComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { getTestId } from '@shared/util/getTestId';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { EditFieldContainerProps } from './EditFieldContainerProps';
import classNames from './EditFieldContainer.module.css';

export function EditFieldContainer({
  component,
  children,
  icon = getComponentIconWithFallback(component),
  iconUrl,
  label = getComponentLabel(component),
  placeholder = getComponentPlaceholder(component),
  footer,
  subfooter,
  notes,
  files,
  showIcon = true,
  showLabel = true,
  showPlaceholder = false,
  showClearButton = false,
  error,
  helpText,
  onPress,
  onClear,
  reserveIconSpace = false,
  testID = getTestId('card-field-cards', component.id),
  anchorName,
  compactTop = false,
  compactBottom = false,
  largerInput = true,
  isGrayScale = false,
  isItalic = false,
  style,
  printContent,
  placeholderColor,
}: EditFieldContainerProps) {
  const colors = useContext(ColorThemeContext);
  const isDesktop = useIsDesktop();
  const isMobile = !isDesktop;
  const isDisplayable = isDisplayableComponent(component);
  const shouldHaveIcon = (showIcon || reserveIconSpace) && isDisplayable;
  const renderIconUrl = showIcon && shouldHaveIcon && !isNil(iconUrl);
  const renderIcon = showIcon && shouldHaveIcon && !isNil(icon) && !renderIconUrl;
  const renderLabel = showLabel && isDisplayable && !isNil(label);
  const renderPlaceholder = showPlaceholder && isDisplayable && !isNil(placeholder);
  const renderClearButton = showClearButton && !isNil(onClear);
  const hasError = !!error;
  const isRequired = 'required' in component && !!component.required;
  let helpContent: string | null = null;
  if (isRequired && helpText) {
    helpContent = i18n('card.form.fieldHelp.requiredWithHelp', { text: helpText });
  } else if (isRequired) {
    helpContent = i18n('card.form.fieldHelp.required');
  } else if (helpText) {
    helpContent = helpText;
  }

  const iconSize = isMobile && largerInput ? grid(4) : grid(3);
  const imageIconSize = isMobile && largerInput ? grid(3) : grid(2.5);

  return (
    <>
      <PrintContent label={label} content={printContent} />
      <View
        testID={testID}
        onClick={onPress}
        className={[
          classNames.container,
          compactTop && classNames.compactTop,
          compactBottom && classNames.compactBottom,
          'no-print',
        ]}
        style={style}
      >
        {renderLabel && (
          <Text size="overline" weight="semibold" danger={hasError} className={classNames.label}>
            {label}
          </Text>
        )}
        <View
          className={[
            classNames.wrapper,
            hasError && classNames.wrapperError,
            isGrayScale && classNames.wrapperGrayScale,
            isItalic && classNames.wrapperItalic,
          ]}
        >
          <View className={[classNames.row, isMobile && largerInput && classNames.rowPadded]}>
            {renderIcon && (
              <View className={classNames.icon}>
                <MBIconV2
                  name={icon}
                  size={iconSize}
                  color={hasError ? colors.error.foreground : colors.accent}
                />
              </View>
            )}
            {renderIconUrl && (
              <View className={classNames.icon}>
                <MBImage
                  source={{ uri: iconUrl }}
                  style={{ width: imageIconSize, height: imageIconSize }}
                />
              </View>
            )}
            {reserveIconSpace && !renderIcon && !renderIconUrl && (
              <View className={classNames.icon} />
            )}
            <View className={classNames.content}>
              {renderPlaceholder && (
                <View
                  className={classNames.placeholder}
                  style={placeholderColor ? { color: placeholderColor } : undefined}
                >
                  {placeholder}
                </View>
              )}
              {children}
            </View>
            {renderClearButton && (
              <View className={classNames.clearButton}>
                <MBClearButton
                  onPress={onClear}
                  color={hasError ? colors.foregroundInactive : undefined}
                />
              </View>
            )}
          </View>
          {notes && <View className={classNames.notes}>{notes}</View>}
          {files && <View className={classNames.files}>{files}</View>}
          {footer && <View className={classNames.footer}>{footer}</View>}
          <View style={{ anchorName }} className={classNames.anchor} />
        </View>
        {subfooter && <View className={classNames.subfooter}>{subfooter}</View>}
        {hasError && (
          <View className={classNames.error}>
            <Text size="body2" danger>
              {error}
            </Text>
          </View>
        )}
        {!hasError && helpContent && (
          <View className={classNames.helpText}>
            <Text size="body2" inactive>
              {helpContent}
            </Text>
          </View>
        )}
      </View>
    </>
  );
}

interface PrintContentProps {
  label?: string | null;
  content?: string | null;
}

export function PrintContent({ label, content }: PrintContentProps) {
  const renderPrintContent = !isNil(content) && !isEmpty(content);

  if (!renderPrintContent) {
    return null;
  }

  return (
    <div className="print-only print-content-section">
      {!isNil(label) && (
        <div>
          <Text size="overline" weight="semibold">
            {label}
          </Text>
        </div>
      )}
      <div>
        <Text size="body1">{content}</Text>
      </div>
    </div>
  );
}
